import { BookOpen, Compass, MessageCircle, Settings2, Sparkles, Users, X } from 'lucide-react'
import { useState } from 'react'

import type { BookAiSection } from '@/api/services/book-ai'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { useBookAiStore } from '@/store/useBookAiStore'
import { useUISettingsStore } from '@/store/useUISettingsStore'

import { AnalysisPanel } from './analysis-panel'
import { BookOverviewTab, type BookOverviewView } from './book-overview-tab'
import { ChatInput, ChatTab } from './chat-tab'
import { GlossaryTab } from './glossary-tab'
import { PurposeChip, PurposePanel } from './purpose-bar'
import { ReaderAiSettings } from './reader-ai-settings'
import { SectionTab } from './section-tab'
import { type BookAiReaderState, useSectionPreviewChip } from './useBookAi'

type BookAiTabValue = 'chapter' | 'book' | 'glossary' | 'chat'

const TABS: { value: BookAiTabValue; label: string; icon: typeof BookOpen }[] = [
  { value: 'chapter', label: 'いまの章', icon: BookOpen },
  { value: 'book', label: '本の全体', icon: Compass },
  { value: 'glossary', label: '人物・用語', icon: Users },
  { value: 'chat', label: '質問', icon: MessageCircle },
]

const isTabValue = (value: string): value is BookAiTabValue => TABS.some((tab) => tab.value === value)

// 広い画面でも 1 行が長くなりすぎないよう、中身の幅を抑えて中央に寄せる
const CONTENT_WIDTH = 'mx-auto w-full max-w-3xl'

/** シートの上部に開く欄（目的の編集・設定。同時には 1 つだけ） */
type HeaderPanel = 'purpose' | 'settings' | null

type SheetProps = {
  bookAi: BookAiReaderState
  open: boolean
  onOpenChange: (open: boolean) => void
  tab: BookAiTabValue
  onTabChange: (tab: BookAiTabValue) => void
  /** 位置キーへ移動する（EPUB: spine 番号 + 進み具合 / PDF: 0 始まりのページ） */
  onNavigate: (key: number) => void
}

const SheetHeader = ({
  bookAi,
  panel,
  onPanelChange,
  onClose,
}: {
  bookAi: BookAiReaderState
  panel: HeaderPanel
  onPanelChange: (panel: HeaderPanel) => void
  onClose: () => void
}) => {
  const hasAnalysis = bookAi.sections.some((s) => s.analysis)
  const toggle = (target: Exclude<HeaderPanel, null>) => onPanelChange(panel === target ? null : target)
  return (
    <div className="border-b border-border">
      <div className={cn(CONTENT_WIDTH, 'space-y-1.5 px-4 pb-2')}>
        <div className="flex items-center gap-1">
          <h2 className="flex-1 text-lg font-semibold">AI 読書アシスト</h2>
          {hasAnalysis && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="表示の設定"
              aria-expanded={panel === 'settings'}
              onClick={() => toggle('settings')}
              className={cn(panel === 'settings' && 'bg-accent')}
            >
              <Settings2 className="size-5" />
            </Button>
          )}
          <Button variant="ghost" size="icon" aria-label="閉じる" onClick={onClose}>
            <X className="size-5" />
          </Button>
        </div>
        {hasAnalysis && (
          <PurposeChip episodeId={bookAi.episodeId} open={panel === 'purpose'} onToggle={() => toggle('purpose')} />
        )}
      </div>
    </div>
  )
}

const BookAiSheet = ({ bookAi, open, onOpenChange, tab, onTabChange, onNavigate }: SheetProps) => {
  const libraryMode = useUISettingsStore((state) => state.bookChatLibraryMode)
  const [panel, setPanel] = useState<HeaderPanel>(null)
  const [overviewView, setOverviewView] = useState<BookOverviewView>('plan')

  const hasAnalysis = bookAi.sections.some((s) => s.analysis)
  const showChatInput = tab === 'chat' && (libraryMode || !!bookAi.status?.chat_ready)

  const handleNavigate = (key: number) => {
    onNavigate(key)
    onOpenChange(false)
  }

  return (
    <BottomSheet
      title="AI 読書アシスト"
      open={open}
      onOpenChange={onOpenChange}
      snapPoints={[0.92]}
      headerContent={
        <SheetHeader bookAi={bookAi} panel={panel} onPanelChange={setPanel} onClose={() => onOpenChange(false)} />
      }
      footer={
        showChatInput ? (
          <div className="border-t bg-background">
            <div className={CONTENT_WIDTH}>
              <ChatInput bookAi={bookAi} />
            </div>
          </div>
        ) : undefined
      }
    >
      <div className={cn(CONTENT_WIDTH, 'space-y-4 px-4 pt-3 pb-4')}>
        <AnalysisPanel bookAi={bookAi} />

        {hasAnalysis && panel === 'settings' && <ReaderAiSettings />}
        {hasAnalysis && panel === 'purpose' && (
          <PurposePanel episodeId={bookAi.episodeId} onClose={() => setPanel(null)} />
        )}

        {hasAnalysis && (
          <Tabs value={tab} onValueChange={(value) => isTabValue(value) && onTabChange(value)}>
            {/* スクロールしても上に残す。タブの地は半透明なので、下に不透明な地を敷いて本文が透けないようにする */}
            <div className="sticky top-0 z-10 -mt-2 bg-background pt-2 pb-1">
              <TabsList className="h-11 w-full">
                {TABS.map(({ value, label, icon: Icon }) => (
                  <TabsTrigger key={value} value={value} className="flex-col gap-0.5 px-1 text-[11px] leading-none">
                    <Icon className="size-4" />
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <TabsContent value="chapter" className="pt-2">
              <SectionTab bookAi={bookAi} onNavigate={handleNavigate} />
            </TabsContent>
            <TabsContent value="book" className="pt-2">
              <BookOverviewTab
                bookAi={bookAi}
                view={overviewView}
                onViewChange={setOverviewView}
                onNavigate={handleNavigate}
              />
            </TabsContent>
            <TabsContent value="glossary" className="pt-2">
              <GlossaryTab bookAi={bookAi} onNavigate={handleNavigate} />
            </TabsContent>
            <TabsContent value="chat" className="pt-2">
              <ChatTab bookAi={bookAi} onNavigate={handleNavigate} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </BottomSheet>
  )
}

const SectionPreviewChip = ({ section, onOpen }: { section: BookAiSection; onOpen: () => void }) => (
  <div className="fixed left-1/2 top-[calc(env(safe-area-inset-top)+0.75rem)] z-40 -translate-x-1/2 animate-in fade-in slide-in-from-top-2">
    <button
      type="button"
      onClick={onOpen}
      className="flex max-w-[86vw] items-center gap-2 rounded-full bg-black/75 px-4 py-2 text-xs text-white shadow-lg backdrop-blur"
    >
      <Sparkles className="size-4 shrink-0 text-amber-300" />
      <span className="truncate">「{section.title}」のポイント</span>
    </button>
  </div>
)

type LayerProps = {
  bookAi: BookAiReaderState
  open: boolean
  onOpenChange: (open: boolean) => void
  onNavigate: (key: number) => void
  /** メニュー表示中など、章のポイントの入口を出したくないとき */
  suppressChip?: boolean
}

/** リーダーに重ねる AI 読書アシスト（章のポイントの入口 + シート） */
export const BookAiLayer = ({ bookAi, open, onOpenChange, onNavigate, suppressChip = false }: LayerProps) => {
  const [tab, setTab] = useState<BookAiTabValue>('chapter')
  // 本文で選んで「AIに質問」したときは質問タブを開く（描画中に状態を合わせる）
  const chatQuote = useBookAiStore((state) => state.chatQuote)
  const [seenQuote, setSeenQuote] = useState<string | null>(null)
  if (chatQuote && chatQuote !== seenQuote) {
    setSeenQuote(chatQuote)
    setTab('chat')
  }
  // 引用を送った（外した）ら忘れる。同じ箇所をもう一度選んで質問したときも質問タブを開くため
  if (!chatQuote && seenQuote) setSeenQuote(null)
  const chipIndex = useSectionPreviewChip(bookAi, open || suppressChip)
  const chipSection = chipIndex == null ? undefined : bookAi.sections.find((s) => s.index === chipIndex)

  return (
    <>
      {chipSection && (
        <SectionPreviewChip
          section={chipSection}
          onOpen={() => {
            setTab('chapter')
            onOpenChange(true)
          }}
        />
      )}
      <BookAiSheet
        bookAi={bookAi}
        open={open}
        onOpenChange={onOpenChange}
        tab={tab}
        onTabChange={setTab}
        onNavigate={onNavigate}
      />
    </>
  )
}
